import { useCallback, useEffect, useMemo } from 'react'
import toast from 'react-hot-toast'
import { Button, GenericFullscreenDialog } from '@/common'
import { DownloadArrowDownIcon } from '@/common/components/icons'
import {
  useDownloadSimulationGoodLiabilityOverviewPdf,
  type BatchLiabilityResultItem
} from '@/features/cbam-reporting/hooks/cbamLiability'
import type { CbamSimulationGood } from '@/features/cbam-simulations-v2/hooks/cbamSimulationGoods'
import type { SupplierEmissionsReport } from '@/features/suppliers/hooks/supplierEmissionsReport'
import type { CbamModifiers } from '@/shared/types/cbamModifiers'
import useI18n from '@/shared/hooks/usei18n'
import { trackCbamSimulationSignal } from '@/features/cbam-simulations-v2/analytics/cbamSimulationClarity'
import {
  buildLiabilityOverviewModel,
  LiabilityOverviewTemplate
} from '@/shared/components/cbam-good-details/liability-overview'
import { useNetMassDisplay } from '@/shared/netMassDisplay/NetMassDisplayUnitContext'

type SimulationGoodLiabilityOverviewDialogProps = {
  isOpen: boolean
  close: () => void
  simulationGood: CbamSimulationGood
  liabilityData: BatchLiabilityResultItem
  supplierEmissionsReport: SupplierEmissionsReport | null
  modifiers: CbamModifiers
  simulationYear: number
  aggregatedGoodsCategory?: string
}

export default function SimulationGoodLiabilityOverviewDialog({
  isOpen,
  close,
  simulationGood,
  liabilityData,
  supplierEmissionsReport,
  modifiers,
  simulationYear,
  aggregatedGoodsCategory
}: SimulationGoodLiabilityOverviewDialogProps) {
  const { t, currentLanguage } = useI18n()
  const { unit: displayUnit } = useNetMassDisplay()

  const { mutateAsync: downloadPdf, isPending: isDownloadingPdf } =
    useDownloadSimulationGoodLiabilityOverviewPdf(simulationGood.id)

  const goodFacts = useMemo(
    () => ({
      quarter: simulationGood.quarter,
      cnCode: simulationGood.cnCode,
      description: simulationGood.description,
      netMassInTonnes: simulationGood.netMassInTonnes,
      isUsingDefaultEmissions: simulationGood.isUsingDefaultEmissions,
      country: simulationGood.country
    }),
    [simulationGood]
  )

  const model = useMemo(
    () =>
      buildLiabilityOverviewModel({
        liabilityData,
        good: goodFacts,
        supplierEmissionsReport,
        modifiers,
        projectionYear: simulationYear,
        aggregatedGoodsCategory,
        t
      }),
    [liabilityData, goodFacts, supplierEmissionsReport, modifiers, simulationYear, aggregatedGoodsCategory, t]
  )

  useEffect(() => {
    if (isOpen) {
      trackCbamSimulationSignal('simulations:good:liability-overview:opened')
    }
  }, [isOpen])

  const onDownloadPdf = useCallback(async () => {
    const cnCode = model?.cnCode ?? simulationGood.cnCode
    const year = model?.year ?? simulationYear
    const filename = `liability-overview-${cnCode}-${year}.pdf`.replace(/\s/g, '_')
    try {
      await downloadPdf({ filename, lang: currentLanguage })
    } catch (err) {
      console.error(err)
      toast.error(t('common:liabilityExplanationDialog.errors.downloadFailed'), {
        position: 'top-center'
      })
    }
  }, [downloadPdf, model, simulationGood.cnCode, simulationYear, currentLanguage, t])

  return (
    <GenericFullscreenDialog
      isOpen={isOpen}
      close={close}
      title={t('common:liabilityExplanationDialog.title')}
      actions={
        <div className="flex items-center gap-2">
          <Button
            variant="subtle"
            icon={<DownloadArrowDownIcon />}
            onClick={onDownloadPdf}
            disabled={isDownloadingPdf || !model}
            isLoading={isDownloadingPdf}
          >
            {t('common:liabilityExplanationDialog.actions.downloadPdf')}
          </Button>
          <Button variant="subtle" onClick={close}>
            {t('common:actions.close')}
          </Button>
        </div>
      }
      containerSize="5xl"
    >
      <LiabilityOverviewTemplate
        model={model}
        originCountryCode={simulationGood.country}
        displayUnit={displayUnit}
      />
    </GenericFullscreenDialog>
  )
}
